import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import L from "leaflet";
import { MapContainer, TileLayer, Polyline, Marker, useMap } from "react-leaflet";
import { Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useConcello, ConcelloLogo } from "@/lib/concellos";
import { CATEGORY, EVENT_TYPES } from "@/lib/constants";
import { SIGNS, SignMini, signQty, signIcon } from "@/components/map/SignObjects";
import { nombresDe, unidadCompleta } from "@/lib/staff";
import { AVISO_DESFASADO } from "@/lib/estadosDesvio";

// Documentos para personal externo a la policía (operarios municipales, organización y colaboradores, Protección Civil):
// solo lo que le toca a cada colectivo —material, horarios, ubicaciones y órdenes—, sin patrullas, agentes ni TIP policiales.
// Se imprimen desde «Documentos»; no dan acceso a la aplicación.

const COLECTIVOS = {
  operarios: {
    titulo: "Montaje y retirada de señalización · Operarios",
    intro: "Material que deben colocar y retirar los operarios municipales, con su ubicación y horario.",
    normas: [
      "Colocar el material en la ubicación indicada dentro de su horario de montaje; cada punto lleva su número en el mapa.",
      "Las señales deben quedar bien visibles para los conductores que se acercan, sin invadir pasos de peatones ni salidas de garajes.",
      "No retirar nada antes de la hora de retirada ni sin la conformidad de la Policía Local.",
      "Ante cualquier incidencia (material dañado, ubicación imposible, vehículo estacionado), avisar a la Policía Local.",
    ],
  },
  organizacion: {
    tipos: ["organizadores"],
    titulo: "Instrucciones para organización y colaboradores",
    intro: "Puestos, horarios y órdenes del personal de la organización y colaboradores del evento.",
    normas: [
      "Presentarse en el puesto a la hora indicada, con chaleco reflectante, y no abandonarlo hasta la hora de fin sin avisar.",
      "Informar y orientar a peatones y conductores. No regular el tráfico rodado: esa función corresponde a la Policía Local.",
      "No retirar vallas ni abrir el paso a vehículos sin autorización de la Policía Local.",
      "Ante cualquier incidencia o emergencia, avisar de inmediato a la Policía Local y facilitar el paso a los servicios de emergencia.",
    ],
  },
  "proteccion-civil": {
    tipos: ["proteccion_civil"],
    titulo: "Instrucciones para Protección Civil",
    intro: "Puestos, horarios y órdenes de los voluntarios de Protección Civil.",
    normas: [
      "Presentarse en el puesto a la hora indicada, con la uniformidad y el equipo de comunicaciones de la agrupación.",
      "Custodiar las vallas y los cortes asignados, impedir el acceso de vehículos y orientar a los peatones.",
      "Seguir en todo momento las indicaciones de la Policía Local; no autorizar el paso de vehículos sin su conformidad.",
      "Ante emergencias, priorizar el paso de servicios sanitarios y bomberos, y comunicar cualquier incidencia a la Policía Local.",
    ],
  },
};

// órdenes por defecto de cada tarea, para personal no policial
const ORDEN = {
  corte_estatico: "Custodia de vallas. No permitir el acceso de vehículos. Ante cualquier incidencia, avisar a la Policía Local.",
  habilitacion: "Habilitar la zona de tránsito especial cuando lo indique la Policía Local y cerrarla al finalizar.",
  senalizacion: "Montaje de la señalización indicada en este punto.",
  desmontaje: "Retirada de la señalización indicada en este punto, al finalizar y con conformidad de la Policía Local.",
};

const osm = (loc) => `https://www.openstreetmap.org/?mlat=${loc[0]}&mlon=${loc[1]}#map=18/${loc[0]}/${loc[1]}`;
const num = (n) => L.divIcon({
  className: "",
  html: `<div class="tac-marker" style="width:22px;height:22px;background:#0f172a;color:#0f172a"><span style="color:#fff">${n}</span></div>`,
  iconSize: [22, 22], iconAnchor: [11, -6],
});

const Fit = ({ pts }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (pts.length > 1) map.fitBounds(pts, { padding: [30, 30], maxZoom: 17 });
      else if (pts.length === 1) map.setView(pts[0], 17);
    }, 150);
    return () => clearTimeout(t);
  }, [map, pts]);
  return null;
};

const Mapa = ({ puntos, signs = [], route }) => (
  <div className="h-[300px] border border-slate-300 rounded overflow-hidden mt-4 relative isolate z-0">
    <MapContainer center={puntos[0] || [42.3929, -8.7042]} zoom={16} maxZoom={22} style={{ height: "100%", width: "100%" }}
      zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} attributionControl={false}>
      <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={22} maxNativeZoom={19} />
      <Fit pts={puntos} />
      {route && <Polyline positions={route} pathOptions={{ color: "#0ea5e9", weight: 4, opacity: 0.35 }} />}
      {signs.map((g) => <Marker key={g.id} position={g.coords[0]} icon={signIcon(g, false)} interactive={false} />)}
      {puntos.map((p, i) => <Marker key={i} position={p} icon={num(i + 1)} interactive={false} />)}
    </MapContainer>
  </div>
);

const Cabecera = ({ ev, concello, c }) => (
  <div className="flex justify-between items-start gap-4 border-b-4 border-blue-800 pb-3">
    <ConcelloLogo ine={ev.concello} className="h-16 w-auto max-w-[6rem] shrink-0" />
    <div className="flex-1">
      <div className="text-[10px] tracking-[0.3em] font-mono text-blue-800">{concello || ev.town?.name ? `AYUNTAMIENTO DE ${(concello?.nombre || ev.town.name).toUpperCase()} · POLICÍA LOCAL` : "POLICÍAS DE GALICIA"}</div>
      <div className="font-display text-2xl font-extrabold mt-1">{c.titulo}</div>
      <div className="text-sm font-semibold">{ev.name}</div>
      <div className="text-xs font-mono text-slate-600">{EVENT_TYPES[ev.type]} · {ev.date} · {ev.start_time}–{ev.end_time}</div>
    </div>
  </div>
);

const Normas = ({ c }) => (
  <div className="text-[11px] text-slate-700 mt-4 border border-slate-300 rounded p-3">
    <div className="font-semibold mb-1">Instrucciones generales</div>
    <ul className="list-disc pl-4 space-y-0.5">{c.normas.map((n) => <li key={n}>{n}</li>)}</ul>
    <div className="mt-2">Teléfono de contacto con la Policía Local: ______________________</div>
  </div>
);

const Material = ({ els }) => (
  <span className="flex flex-col gap-0.5">
    {els.map((e) => (
      <span key={e.id} className="inline-flex items-center gap-1.5"><SignMini type={e.sign_type} rot={e.rotation} size={14} />{SIGNS[e.sign_type]?.label || "Objeto"}{signQty(e) > 1 ? ` x${signQty(e)}` : ""}</span>
    ))}
  </span>
);

// Operarios: cada punto con su material, montaje y retirada (solo lo que tienen asignado los operarios)
const Operarios = ({ ev, c, concello }) => {
  const tasks = (ev.plan.tasks || []).filter((t) => t.element_ids?.length && t.forzado === "operarios");
  const puntos = [];
  tasks.sort((a, b) => a.start - b.start).forEach((t) => {
    const k = t.element_ids.join("|");
    let p = puntos.find((x) => x.k === k);
    if (!p) { p = { k, t, els: t.element_ids.map((id) => ev.elements.find((e) => e.id === id)).filter(Boolean) }; puntos.push(p); }
    p[t.category] = t;
  });
  const total = {};
  puntos.forEach((p) => p.els.forEach((e) => { const l = SIGNS[e.sign_type]?.label || "Objeto"; total[l] = (total[l] || 0) + signQty(e); }));
  const route = ev.elements.find((e) => e.kind === "route")?.coords;
  return (
    <div className="bg-white text-slate-900 p-10 shadow" data-testid="doc-operarios">
      <Cabecera ev={ev} concello={concello} c={c} />
      <p className="text-xs text-slate-600 mt-3">{c.intro}</p>
      {!puntos.length ? <p className="mt-6 text-sm text-slate-600" data-testid="doc-vacio">No hay material asignado a los operarios. En la ficha de cada objeto, elija «Operarios» en Monta o Retira.</p> : (
        <>
          <div className="mt-4 text-[11px]" data-testid="doc-material-total"><b>Material total:</b> {Object.entries(total).map(([l, n]) => `${l} x${n}`).join(" · ")}</div>
          <Mapa puntos={puntos.map((p) => p.t.loc)} signs={puntos.flatMap((p) => p.els)} route={route} />
          <table className="w-full text-[11px] border-collapse mt-4">
            <thead><tr>{["Nº", "Ubicación", "Material", "Montaje", "Retirada"].map((h) => <th key={h} className="text-left bg-slate-200 px-2 py-1.5 border border-slate-300">{h}</th>)}</tr></thead>
            <tbody>
              {puntos.map((p, i) => (
                <tr key={p.k} className="align-top">
                  <td className="px-2 py-1.5 border border-slate-300 font-mono">{i + 1}</td>
                  <td className="px-2 py-1.5 border border-slate-300">
                    <div className="font-semibold">{ev.plan.ubicaciones?.[p.els[0]?.id] || (p.els[0]?.name || "").replace(/^[^·]*·\s*/, "")}</div>
                    <a href={osm(p.t.loc)} className="font-mono text-[10px] text-blue-700 underline">{p.t.loc[0].toFixed(5)}, {p.t.loc[1].toFixed(5)}</a>
                    {p.els.map((e) => e.notes).filter(Boolean).map((n, j) => <div key={j} className="mt-1 border-l-4 border-blue-800 pl-2"><b>Órdenes:</b> {n}</div>)}
                  </td>
                  <td className="px-2 py-1.5 border border-slate-300"><Material els={p.els} /></td>
                  <td className="px-2 py-1.5 border border-slate-300 font-mono whitespace-nowrap">{p.senalizacion ? `${p.senalizacion.start_hm}–${p.senalizacion.end_hm}` : <span className="font-sans text-slate-500">otro servicio</span>}</td>
                  <td className="px-2 py-1.5 border border-slate-300 font-mono whitespace-nowrap">{p.desmontaje ? `desde ${p.desmontaje.start_hm}` : <span className="font-sans text-slate-500">otro servicio</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <Normas c={c} />
    </div>
  );
};

// Organización / Protección Civil: una ficha por persona o grupo, con sus puestos y órdenes
const Personal = ({ ev, c, concello }) => {
  const nombres = nombresDe(ev);
  const units = (ev.plan.units || []).filter((u) => c.tipos.includes(u.type) && u.segments.length).map((u) => ({ ...u, ...nombres[u.id] }));
  const signs = ev.elements.filter((e) => e.kind === "sign" && e.coords?.length);
  const route = ev.elements.find((e) => e.kind === "route")?.coords;
  if (!units.length) return (
    <div className="bg-white text-slate-900 p-10 shadow">
      <Cabecera ev={ev} concello={concello} c={c} />
      <p className="mt-6 text-sm text-slate-600" data-testid="doc-vacio">No hay personal de este colectivo con puestos en el mapa. Dé de alta el grupo en «Personal» y colóquelo en el mapa.</p>
      <Normas c={c} />
    </div>
  );
  return units.map((u, i) => {
    const propios = (ev.plan.tasks || []).filter((t) => t.element_ids && (t.assigned || []).includes(u.id)).flatMap((t) => t.element_ids);
    return (
      <div key={u.id} className={`bg-white text-slate-900 p-10 mb-6 shadow ${i ? "print-break" : ""}`} data-testid={`doc-unidad-${u.id}`}>
        <Cabecera ev={ev} concello={concello} c={c} />
        <div className="grid grid-cols-3 gap-4 text-xs mt-4">
          <div><span className="text-slate-500">Puesto de:</span> <b>{unidadCompleta(u.tipo, u.nombre)}</b>{u.tip && !u.nombre?.startsWith("TIP") ? ` · ${u.tip}` : ""}</div>
          <div><span className="text-slate-500">Turno:</span> <b className="font-mono">{u.shift_hm}</b></div>
          {u.nota && <div><span className="text-slate-500">Notas:</span> {u.nota}</div>}
        </div>
        <Mapa puntos={u.segments.filter((s) => s.loc).map((s) => s.loc)} signs={signs.filter((g) => propios.includes(g.id))} route={route} />
        <table className="w-full text-[11px] border-collapse mt-4">
          <thead><tr>{["Hora", "Puesto y órdenes", "Ubicación"].map((h) => <th key={h} className="text-left bg-slate-200 px-2 py-1.5 border border-slate-300">{h}</th>)}</tr></thead>
          <tbody>
            {u.segments.map((s, j) => (
              <tr key={j} className="align-top">
                <td className="px-2 py-1.5 border border-slate-300 font-mono whitespace-nowrap"><span className="inline-block w-4 text-slate-500">{j + 1}.</span><b>{s.start_hm}</b>{s.category === "asignacion" ? <div className="text-[9px] text-slate-500 pl-4">hasta {s.end_hm}</div> : `–${s.end_hm}`}</td>
                <td className="px-2 py-1.5 border border-slate-300">
                  <div className="font-semibold">{s.name}{s.handover ? " · RELEVO" : ""}</div>
                  {s.category !== "asignacion" && <div className="text-slate-600">{CATEGORY[s.category]?.label}</div>}
                  {s.notes ? <div className="mt-1 border-l-4 border-blue-800 pl-2 whitespace-pre-wrap"><b>Órdenes:</b> {s.notes}</div>
                    : ORDEN[s.category] && <div className="text-slate-600 mt-1 text-[10px]">{ORDEN[s.category]}</div>}
                </td>
                <td className="px-2 py-1.5 border border-slate-300 font-mono text-[10px]">{s.loc ? <a href={osm(s.loc)} className="text-blue-700 underline">{s.loc[0].toFixed(5)}, {s.loc[1].toFixed(5)}</a> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {propios.length > 0 && (
          <div className="mt-4 text-[11px]"><b>Material que le corresponde montar o retirar:</b>
            <div className="mt-1"><Material els={signs.filter((g) => propios.includes(g.id))} /></div>
          </div>
        )}
        <Normas c={c} />
      </div>
    );
  });
};

export default function DocumentosExternos() {
  const { id, colectivo } = useParams();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => setErr(true)); }, [id]);
  const concello = useConcello(ev?.concello);
  const c = COLECTIVOS[colectivo];
  if (err || !c) return <div className="p-10 bg-white min-h-screen text-slate-800">Documento no encontrado.</div>;
  if (!ev) return <div className="p-10 text-slate-500">Cargando…</div>;
  if (!ev.plan) return <div className="p-10 bg-white min-h-screen text-slate-800">Genere primero el plan de tráfico.</div>;
  return (
    <div className="bg-slate-100 min-h-screen">
      <div className="no-print sticky top-0 z-[1000] bg-[#0b1329] text-white px-6 py-3 flex items-center justify-between">
        <span className="text-sm">{c.titulo} · para personal externo: sin patrullas ni agentes</span>
        <button onClick={() => window.print()} className="flex items-center gap-2 bg-sky-500 text-slate-950 font-semibold px-4 py-2 rounded-lg text-sm" data-testid="print-doc-button">
          <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
        </button>
      </div>
      <div className="print-page max-w-[900px] mx-auto py-6" data-testid={`doc-${colectivo}`}>
        {ev.plan_vigente === false && <div className="no-print mb-4 border-2 border-red-600 bg-red-50 text-red-800 text-sm font-semibold rounded-lg px-4 py-3">{AVISO_DESFASADO}</div>}
        {colectivo === "operarios" ? <Operarios ev={ev} c={c} concello={concello} /> : <Personal ev={ev} c={c} concello={concello} />}
      </div>
    </div>
  );
}
